/**
 * useVoiceInput.js — Speech-to-Text Hook
 *
 * Uses the browser's built-in Web Speech API (SpeechRecognition).
 * No external service needed for demo — works in Chrome and Edge out of the box.
 *
 * TODO (Phase 3 — Production):
 *   For production with better accuracy and language support, replace with:
 *   - Azure Cognitive Services Speech SDK
 *   - Google Cloud Speech-to-Text API
 *   - AWS Transcribe Streaming
 *
 *   These services require:
 *   1. An API key (stored in your backend — never in frontend code)
 *   2. Your backend receives audio, calls the cloud STT, returns text
 *   3. This hook would then POST audio to your backend instead of using browser API
 *
 * Browser support:
 *   Chrome ✅  Edge ✅  Firefox ⚠️ (partial)  Safari ⚠️ (partial)
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import logger from '../utils/logger';

/**
 * @param {function} onResult - Callback called with the transcribed text string
 * @returns {object} Voice input controls and state
 */
export function useVoiceInput(onResult) {
  const [isListening,   setIsListening]   = useState(false);
  const [isSupported,   setIsSupported]   = useState(false);
  const [error,         setError]         = useState(null);
  const [interimText,   setInterimText]   = useState(''); // Partial real-time transcript

  const recognitionRef = useRef(null);

  // ── Check browser support on mount ─────────────────────────────────────────
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      setIsSupported(true);
      logger.info('VoiceInput: Web Speech API supported');
    } else {
      setIsSupported(false);
      logger.warn('VoiceInput: Web Speech API not supported in this browser');
    }

    // Cleanup: stop recognition when component unmounts
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // ── Start listening ─────────────────────────────────────────────────────────
  const startListening = useCallback(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setError('Voice input is not supported in your browser. Please use Chrome or Edge.');
      return;
    }

    setError(null);
    setInterimText('');

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    // Configuration
    recognition.continuous      = false; // Stop after first pause
    recognition.interimResults  = true;  // Show partial results while speaking
    recognition.lang            = 'en-IN'; // Indian English — change as needed
    recognition.maxAlternatives = 1;

    // ── Event handlers ──────────────────────────────────────────────────────

    recognition.onstart = () => {
      setIsListening(true);
      logger.info('VoiceInput: Recording started');
    };

    recognition.onresult = (event) => {
      let interim = '';
      let final   = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }

      setInterimText(interim);

      if (final) {
        logger.info('VoiceInput: Final transcript received', { text: final });
        onResult(final.trim());
        setInterimText('');
      }
    };

    recognition.onerror = (event) => {
      logger.error('VoiceInput: Recognition error', { error: event.error });

      const errorMessages = {
        'not-allowed':       'Microphone permission denied. Please allow microphone access.',
        'no-speech':         'No speech detected. Please try again.',
        'audio-capture':     'Microphone not found. Please check your microphone.',
        'network':           'Network error during voice recognition. Please check connection.',
        'aborted':           'Voice input was cancelled.',
      };

      setError(errorMessages[event.error] || `Voice input error: ${event.error}`);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimText('');
      logger.info('VoiceInput: Recording ended');
    };

    // Start recording
    try {
      recognition.start();
    } catch (err) {
      logger.error('VoiceInput: Could not start recognition', err);
      setError('Could not start voice input. Please try again.');
      setIsListening(false);
    }
  }, [onResult]);

  // ── Stop listening ──────────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      logger.info('VoiceInput: Stopped manually');
    }
    setIsListening(false);
  }, []);

  // ── Toggle ──────────────────────────────────────────────────────────────────
  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    isListening,    // boolean — true while recording
    isSupported,    // boolean — false if browser doesn't support
    error,          // string | null — error message
    interimText,    // string — partial transcript shown while speaking
    startListening,
    stopListening,
    toggleListening,
  };
}
